import { A } from '@solidjs/router';
import { createEffect, createSignal, onCleanup, Show } from 'solid-js';
import PublicLayout from '../PublicLayout';

export default function CrystalBallPostVi() {
  const [heroFailed, setHeroFailed] = createSignal(false);
  const [heatFailed, setHeatFailed] = createSignal(false);
  const [dashFailed, setDashFailed] = createSignal(false);

  createEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline:
        'Quả Cầu Pha Lê Kho Hàng: Dùng AI Dự Đoán Nhu Cầu Trước Khi Nó Xảy Ra',
      description:
        'Chán cảnh hết hàng bestseller trong khi hàng chậm chất đống? Tìm hiểu cách AI tính tốc độ bán hàng theo từng SKU, đưa ra dự báo kèm điểm tin cậy, và tự động chuyển đổi dự báo thành đơn đặt hàng nhà cung cấp chỉ với một cú nhấp.',
      datePublished: '2026-05-09',
      dateModified: '2026-05-09',
      image: 'https://khoai.io/blog-forecast-hero.jpg',
      url: 'https://khoai.io/vi/blogs/crystal-ball',
      inLanguage: 'vi',
      author: {
        '@type': 'Organization',
        name: 'khoai.io',
        url: 'https://khoai.io',
      },
      publisher: {
        '@type': 'Organization',
        name: 'khoai.io',
        url: 'https://khoai.io',
        logo: {
          '@type': 'ImageObject',
          url: 'https://khoai.io/og-welcome.jpg',
        },
      },
      isPartOf: {
        '@type': 'Blog',
        name: 'Khoai Blog',
        url: 'https://khoai.io/vi/blogs',
      },
    };

    let script = document.getElementById(
      'ld-json-blog-crystal-ball-vi'
    ) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'ld-json-blog-crystal-ball-vi';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);

    let bcScript = document.getElementById(
      'ld-json-blog-crystal-ball-vi-bc'
    ) as HTMLScriptElement | null;
    if (!bcScript) {
      bcScript = document.createElement('script');
      bcScript.type = 'application/ld+json';
      bcScript.id = 'ld-json-blog-crystal-ball-vi-bc';
      document.head.appendChild(bcScript);
    }
    bcScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Trang chủ',
          item: 'https://khoai.io',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Blog',
          item: 'https://khoai.io/vi/blogs',
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: schema.headline,
          item: schema.url,
        },
      ],
    });
  });

  onCleanup(() => {
    document.getElementById('ld-json-blog-crystal-ball-vi')?.remove();
    document.getElementById('ld-json-blog-crystal-ball-vi-bc')?.remove();
  });

  const h2 = 'mt-12 text-2xl font-extrabold text-gray-900 sm:text-3xl';
  const h3 = 'mt-8 text-xl font-bold text-gray-900';
  const p = 'mt-4 text-base leading-8 text-gray-600';
  const ul = 'mt-4 list-disc space-y-2 pl-6 text-base leading-7 text-gray-600';
  const imgFallback =
    'flex min-h-[320px] w-full items-center justify-center rounded-2xl bg-gray-100 text-sm text-gray-400';

  return (
    <PublicLayout>
      <article class="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav class="mb-8 flex items-center gap-2 text-sm text-gray-400">
          <A href="/vi/blogs" class="hover:text-amber-600">
            Blog
          </A>
          <span>/</span>
          <span class="text-gray-600">AI &amp; Dự Báo</span>
        </nav>

        {/* Header */}
        <header>
          <span class="rounded-full bg-amber-100 px-3 py-0.5 text-xs font-semibold text-amber-700">
            AI &amp; Dự Báo
          </span>
          <h1 class="mt-4 text-3xl font-extrabold leading-tight text-gray-900 sm:text-4xl lg:text-5xl">
            Quả Cầu Pha Lê Kho Hàng: Dùng AI Dự Đoán Nhu Cầu Trước Khi Nó Xảy Ra
          </h1>
          <p class="mt-4 text-lg leading-relaxed text-gray-500">
            Chán cảnh hết hàng bestseller trong khi hàng chậm chất đống? Tìm
            hiểu cách AI tính tốc độ bán hàng theo từng SKU, đưa ra dự báo kèm
            điểm tin cậy, và tự động chuyển đổi dự báo thành đơn đặt hàng nhà
            cung cấp chỉ với một cú nhấp.
          </p>
          <div class="mt-5 flex items-center gap-4 text-sm text-gray-400">
            <span>9 tháng 5, 2026</span>
            <span>·</span>
            <span>9 phút đọc</span>
          </div>
        </header>

        {/* Hero image */}
        <div class="mt-10 overflow-hidden rounded-2xl">
          <Show
            when={!heroFailed()}
            fallback={
              <div class={imgFallback} style={{ 'min-height': '340px' }}>
                Màn hình kiosk hiển thị biểu đồ dự báo nhu cầu trong kho hàng
              </div>
            }
          >
            <img
              src="/blog-forecast-hero.jpg"
              alt="Màn hình kiosk di động được chiếu sáng trong kho hàng tối, hiển thị biểu đồ dự báo màu sắc với đường cầu tăng vọt được ghi nhãn 'Tuần Tới: +40% Độ Tin Cậy'"
              width={1200}
              height={628}
              class="w-full object-cover"
              onError={() => setHeroFailed(true)}
            />
          </Show>
        </div>

        {/* ── Body ── */}
        <div class="mt-12">
          {/* Hook */}
          <p class={p}>
            Thứ Tư chiều. Bạn đứng trước Kệ B-07 — nơi chứa SKU bán chạy nhất —
            nhìn vào khoảng không trống rỗng. Kệ trống từ Chủ nhật. Ba ngày liên
            tiếp, khách hàng nghe câu &ldquo;hết hàng.&rdquo; Lô hàng thay thế
            sớm nhất cũng phải Thứ Sáu mới về.
          </p>
          <p class={p}>
            Hai dãy kệ bên kia là pallet hàng nhập thừa từ bốn tháng trước, chưa
            bán được một đơn nào. Bạn trả tiền lưu kho. Bạn trả phí vận chuyển
            khi nhập. Và bạn vẫn đang trả cả hai khoản đó mỗi ngày nó chưa ra
            khỏi kho.
          </p>
          <p class={p}>
            Đây là{' '}
            <strong class="font-semibold text-gray-900">
              cái bẫy hai lưỡi của quản lý kho bị động
            </strong>
            : hết hàng thứ bán được, ứ đọng thứ không ai mua. Đây không phải lỗi
            mua hàng — đây là lỗi <em>hiển thị dữ liệu</em>, và đó chính xác là
            vấn đề mà dự báo AI được thiết kế để loại bỏ.
          </p>

          {/* Section 1: The Mismatch Penalty */}
          <h2 class={h2}>Chi Phí Dự Báo Sai: Tính Toán Thiệt Hại Thực Tế</h2>
          <p class={p}>
            Hầu hết chủ kho xem hết hàng và tồn kho thừa là những bất tiện thỉnh
            thoảng xảy ra. Nhưng đó là một khoản thuế liên tục, có thể đo đếm
            được, ăn dần vào biên lợi nhuận của bạn.
          </p>

          <div class="mt-8 grid gap-4 sm:grid-cols-2">
            <div class="rounded-2xl border border-red-200 bg-red-50 p-5">
              <p class="text-sm font-bold uppercase tracking-wide text-red-500">
                Chi phí hết hàng
              </p>
              <ul class="mt-3 space-y-1.5 text-sm leading-6 text-gray-700">
                <li>✗ Mất doanh thu ngay lập tức</li>
                <li>✗ Khách chuyển sang đối thủ cạnh tranh</li>
                <li>✗ Chi phí vận chuyển gấp để bù hàng</li>
                <li>✗ Đánh giá tiêu cực hoặc yêu cầu hoàn tiền</li>
                <li>✗ Thời gian nhân viên xử lý khiếu nại</li>
              </ul>
            </div>
            <div class="rounded-2xl border border-blue-200 bg-blue-50 p-5">
              <p class="text-sm font-bold uppercase tracking-wide text-blue-500">
                Chi phí tồn kho thừa
              </p>
              <ul class="mt-3 space-y-1.5 text-sm leading-6 text-gray-700">
                <li>✗ Vốn bị trói buộc trong hàng chưa bán</li>
                <li>✗ Chi phí lưu kho theo m² mỗi tháng</li>
                <li>✗ Nguy cơ hư hỏng hoặc lỗi thời</li>
                <li>✗ Phí bảo hiểm cho hàng tồn kho thừa</li>
                <li>✗ Áp lực giảm giá để giải phóng hàng</li>
              </ul>
            </div>
          </div>

          <p class={p}>
            Phép tính thực tế: một SKU bestseller hết hàng ba ngày, với 20 đơn
            mỗi ngày, giá trị đơn trung bình 500.000 VND — là 30.000.000 VND
            doanh thu bay đi trong một tuần. Không phải tháng tệ. Chỉ một kệ
            hàng. Một tuần.
          </p>
          <p class={p}>
            Mục tiêu của dự báo AI không phải là một tính năng thời thượng — mà
            là kéo con số đó về gần bằng không.
          </p>

          {/* Section 2: Why Averages Fail */}
          <h2 class={h2}>Tại Sao Số Liệu Trung Bình Tháng Trước Là Sai Số</h2>
          <p class={p}>
            Logic đặt hàng thủ công truyền thống hoạt động như thế này: tính
            doanh số trung bình hàng tháng, đặt điểm tái nhập kho ở mức &ldquo;2
            tuần tồn kho,&rdquo; và kiểm tra thủ công vào cuối mỗi tháng. Hợp lý
            vào năm 2005. Nguy hiểm vào năm 2026.
          </p>
          <p class={p}>
            Lý do: số trung bình san phẳng tất cả — kể cả những tín hiệu quan
            trọng nhất:
          </p>
          <ul class={ul}>
            <li>
              Sản phẩm bán được 10 đơn tháng Một, 10 đơn tháng Hai, 80 đơn tháng
              Ba sẽ có &ldquo;trung bình&rdquo; là 33 đơn — con số không phản
              ánh đúng bất kỳ tháng nào.
            </li>
            <li>
              Một ngày lễ địa phương tăng gấp đôi lưu lượng khách trong hai tuần
              sẽ không xuất hiện trong dữ liệu năm ngoái nếu bạn đóng cửa hoặc
              quy mô nhỏ hơn.
            </li>
            <li>
              Một đối thủ đóng cửa quý trước đẩy làn sóng khách mới về phía bạn
              — sự kiện một lần mà mô hình xu hướng đơn giản sẽ coi là vĩnh
              viễn.
            </li>
          </ul>
          <p class={p}>
            Dự báo bằng AI không dùng một mức trung bình cố định. Nó liên tục
            tính lại{' '}
            <strong class="font-semibold text-gray-900">
              tốc độ bán hàng theo từng SKU
            </strong>{' '}
            — sản phẩm đó đang di chuyển nhanh như thế nào <em>ngay lúc này</em>
            , có trọng số theo mùa vụ, biến động xu hướng gần đây và thời gian
            giao hàng của nhà cung cấp — tất cả cùng lúc.
          </p>

          {/* Heat map image */}
          <div class="mt-10 overflow-hidden rounded-2xl">
            <Show
              when={!heatFailed()}
              fallback={
                <div class={imgFallback} style={{ 'min-height': '280px' }}>
                  Bản đồ nhiệt kho hàng 3D isometric hiển thị SKU nóng gần trạm
                  đóng gói
                </div>
              }
            >
              <img
                src="/blog-velocity-heat.jpg"
                alt="Góc nhìn 3D isometric bản đồ kho hàng trên máy tính bảng. SKU bán chạy gần trạm đóng gói phát sáng đỏ/cam; hàng chậm màu xanh lạnh. Mũi tên chỉ từ Vùng Nóng đến Trạm Đóng Gói."
                width={800}
                height={500}
                class="w-full object-cover"
                loading="lazy"
                onError={() => setHeatFailed(true)}
              />
            </Show>
          </div>

          {/* Section 3: Dynamic Velocity */}
          <h2 class={h2}>Tốc Độ Động: Con Số Thay Đổi Mỗi Ngày</h2>
          <p class={p}>
            Tốc độ không phải là thuộc tính cố định của sản phẩm — đó là một
            phép đo liên tục. Sản phẩm bán 2 đơn/ngày tháng trước có thể đang
            chạy 8 đơn/ngày tuần này sau một đề cập trên mạng xã hội từ KOL địa
            phương. Sản phẩm luôn bán tốt vào mùa hè có thể đã bắt đầu giảm khi
            thu đến.
          </p>
          <p class={p}>
            Khi hệ thống theo dõi tốc độ theo thời gian thực, điểm tái nhập kho
            không được tính một lần mỗi tháng — chúng cập nhật liên tục. Hệ
            thống không chờ bạn nhận ra kệ gần hết hàng. Nó tính toán:{' '}
            <em>
              &ldquo;Với tốc độ hiện tại, SKU này sẽ về 0 sau 4 ngày. Thời gian
              giao hàng của nhà cung cấp là 5 ngày. Đã đến lúc đặt hàng.&rdquo;
            </em>
          </p>
          <p class={p}>
            Sự chuyển đổi đó — từ{' '}
            <strong class="font-semibold text-gray-900">
              hết hàng bị động sang nhập hàng chủ động
            </strong>{' '}
            — là toàn bộ giá trị cốt lõi của quản lý kho bằng AI.
          </p>

          {/* Technical tidbit */}
          <h3 class={h3}>Những Quy Luật Ẩn Mà Bảng Tính Không Thể Tìm Ra</h3>
          <p class={p}>
            Đây là loại insight minh họa hướng phát triển của AI kho hàng — và
            lý do khoảng cách giữa quản lý bảng tính và hệ thống dựa trên dữ
            liệu ngày càng lớn:
          </p>
          <div class="mt-6 rounded-2xl border border-gray-200 bg-gray-50 p-6">
            <p class="text-sm font-bold uppercase tracking-wide text-gray-500">
              Loại quy luật mà AI nâng cao có thể phát hiện
            </p>
            <p class="mt-3 text-base leading-8 text-gray-700">
              &ldquo;Khi Sản Phẩm A (dung dịch tẩy rửa cao cấp) bán vào Thứ Ba,
              Sản Phẩm B (gói nạp lại cùng dòng) luôn tăng đột biến vào Thứ Năm
              — với tương quan 78% trong 6 tháng qua.&rdquo;
            </p>
            <p class="mt-3 text-sm text-gray-500">
              Người duyệt bảng tính thấy hai SKU riêng biệt. Một hệ thống AI
              nhìn thấy mô hình mua hàng theo cặp với độ trễ 48 giờ — và có thể
              dùng đó làm tín hiệu tái nhập kho trước khi kệ trống. Đây là hướng
              phát triển tiếp theo của trí tuệ kho hàng.
            </p>
          </div>
          <p class={p}>
            Danh mục của bạn có thể chứa hàng chục quy luật tương tự: bộ sản
            phẩm theo mùa, hàng bổ sung nhau, các SKU cùng phản ứng với một
            chương trình khuyến mãi. Bước đầu tiên để phát hiện chúng là có dữ
            liệu tốc độ bán hàng chính xác theo từng SKU — đúng là điểm khởi đầu
            của Khoai.
          </p>

          {/* Section 4: Supplier Reliability Score */}
          <h2 class={h2}>
            Điểm Tin Cậy Nhà Cung Cấp: Biết Chính Xác Ai Đáng Tin
          </h2>
          <p class={p}>
            Đây là quyết định mà mọi quản lý kho đều đưa ra theo cảm tính nhưng
            hiếm khi đo lường được: nhà cung cấp nói giao hàng trong 5 ngày. Bạn
            có tin không? Bạn đặt hàng trước 6 ngày hay 12 ngày — chỉ để chắc
            chắn?
          </p>
          <p class={p}>
            Khoai tính toán{' '}
            <strong class="font-semibold text-gray-900">
              điểm tin cậy nhà cung cấp (0–100)
            </strong>{' '}
            cho từng nhà cung cấp của bạn, dựa trên mức độ nhất quán của thời
            gian giao hàng thực tế trong lịch sử. Nhà cung cấp luôn giao trong
            4–6 ngày đạt điểm gần 100. Nhà cung cấp có thời gian giao hàng dao
            động từ 3 đến 18 ngày sẽ có điểm thấp hơn nhiều.
          </p>
          <p class={p}>
            Điểm đó tác động trực tiếp và cụ thể lên cách tính điểm tái nhập kho
            của bạn:
          </p>
          <ul class={ul}>
            <li>
              <strong class="font-semibold text-gray-900">
                Tin cậy cao (80–100):
              </strong>{' '}
              cửa sổ tái nhập kho được tính theo thời gian giao hàng đã công bố
              — bạn có thể để sát nút.
            </li>
            <li>
              <strong class="font-semibold text-gray-900">
                Tin cậy trung bình (40–79):
              </strong>{' '}
              hệ thống tự động áp thêm biên an toàn — cảnh báo tái nhập kho kích
              hoạt sớm hơn bạn nghĩ.
            </li>
            <li>
              <strong class="font-semibold text-gray-900">
                Tin cậy thấp (dưới 40):
              </strong>{' '}
              mức độ khẩn cấp được kích hoạt sớm hơn và số lượng đặt hàng đề
              xuất tăng lên để bù cho nguy cơ giao hàng trễ.
            </li>
          </ul>
          <p class={p}>
            Kết quả: bạn không còn phải phán đoán bằng linh cảm về việc tuần này
            có thể tin nhà cung cấp nào. Dữ liệu nói thay — và logic tái nhập
            kho tự điều chỉnh theo.
          </p>

          {/* Dashboard image */}
          <div class="mt-10 overflow-hidden rounded-2xl">
            <Show
              when={!dashFailed()}
              fallback={
                <div class={imgFallback} style={{ 'min-height': '280px' }}>
                  Infographic UI phẳng hiển thị công cụ dự báo AI với các đầu ra
                </div>
              }
            >
              <img
                src="/blog-ai-dashboard.jpg"
                alt="Infographic thiết kế phẳng. Biểu tượng não Công Cụ Dự Báo trung tâm nhận các đường dữ liệu từ Lịch Sử Bán Hàng, Thời Gian Giao Hàng và Sự Kiện Địa Phương, xuất ra hai ô: Đề Xuất PO: 50 Đơn Vị và Cảnh Báo Tồn Kho An Toàn."
                width={800}
                height={500}
                class="w-full object-cover"
                loading="lazy"
                onError={() => setDashFailed(true)}
              />
            </Show>
          </div>

          {/* Section 5: Automated POs */}
          <h2 class={h2}>
            Từ Dự Báo Đến Đơn Đặt Hàng: Hoàn Tất Vòng Lặp Với Một Cú Nhấp
          </h2>
          <p class={p}>
            Biết cần nhập hàng chỉ là một nửa công việc. Nửa còn lại là thực sự
            làm — mà trong hầu hết các quy trình thủ công có nghĩa là mở liên hệ
            nhà cung cấp, viết yêu cầu, kiểm tra chéo mặt hàng sắp hết, xác nhận
            lịch giao hàng. Với kho có 200 SKU đang hoạt động, quy trình đó tiêu
            tốn nhiều giờ mỗi tuần.
          </p>
          <p class={p}>
            Khi phân tích AI được kết nối trực tiếp với danh mục nhà cung cấp,
            dự báo trở thành đơn đặt hàng đã điền sẵn: SKU, số lượng, nhà cung
            cấp ưu tiên, thời gian giao hàng dự kiến. Người quản lý xem lại,
            điều chỉnh nếu cần, và phê duyệt chỉ với một xác nhận. Công việc
            chuẩn bị được thực hiện tự động.
          </p>
          <p class={p}>
            Kết quả không chỉ là hiệu quả — mà còn là{' '}
            <strong class="font-semibold text-gray-900">tính nhất quán</strong>.
            Mua hàng thủ công phụ thuộc vào người tình cờ thực hiện đặt hàng
            tuần đó và họ nhớ kiểm tra gì. Mua hàng tự động dựa trên AI nhất
            quán mọi lúc, cho mọi SKU, bất kể ai đang trực.
          </p>

          {/* Section 6: Start Small */}
          <h2 class={h2}>Không Cần Danh Mục Lớn Để Bắt Đầu</h2>
          <p class={p}>
            Một lo ngại phổ biến: &ldquo;chúng tôi chưa có đủ dữ liệu để AI hoạt
            động hiệu quả.&rdquo; Điều này đúng khi huấn luyện mô hình từ đầu —
            nhưng không đúng với hệ thống đã tích hợp sẵn mô hình dự báo được
            tinh chỉnh theo mô hình bán lẻ chung.
          </p>
          <p class={p}>
            Cách tiếp cận thực tế: bắt đầu với 20 SKU bán chạy nhất. Chạy theo
            dõi tốc độ và cảnh báo tái nhập kho cho những sản phẩm đó. Sau hai
            đến bốn tuần, hệ thống đã có đủ dữ liệu thực của bạn để cải thiện
            đáng kể dự báo cho những mặt hàng cụ thể đó. Sau đó mở rộng ra 50
            SKU tiếp theo.
          </p>
          <p class={p}>
            Dự báo AI không phải là biến đổi tức thì. Đó là lợi thế cộng dồn —
            càng chạy lâu với dữ liệu vận hành thực của bạn, độ chính xác càng
            cao. Thời điểm tốt nhất để bắt đầu là sáu tháng trước. Tốt nhất tiếp
            theo là hôm nay.
          </p>

          {/* CTA */}
          <div class="mt-14 rounded-2xl bg-amber-600 px-8 py-10 text-center text-white">
            <h2 class="text-2xl font-extrabold">
              Ngừng phản ứng. Bắt đầu dự đoán.
            </h2>
            <p class="mt-3 text-base leading-7 text-amber-100">
              Phân tích AI của Khoai theo dõi tốc độ bán hàng theo thời gian
              thực, cảnh báo cơ hội nhập hàng trước khi hết kho, và tự động tạo
              đơn đặt hàng nhà cung cấp. Dùng thử miễn phí — không cần thẻ tín
              dụng.
            </p>
            <A
              href="/signup"
              class="mt-6 inline-block rounded-xl bg-white px-8 py-3 text-base font-bold text-amber-700 hover:bg-amber-50"
            >
              Dùng thử Khoai miễn phí →
            </A>
          </div>
        </div>

        {/* Back to blog */}
        <div class="mt-16 border-t border-gray-200 pt-8">
          <A
            href="/vi/blogs"
            class="text-sm font-semibold text-amber-600 hover:text-amber-700"
          >
            ← Quay lại tất cả bài viết
          </A>
        </div>
      </article>
    </PublicLayout>
  );
}
